import { Handle, Position, NodeResizer } from 'reactflow';
import { type LucideIcon, Loader2, CheckCircle2, XCircle } from 'lucide-react';

interface BaseNodeUIProps {
    title: string;
    icon: LucideIcon;
    colorClass: string; // Used for Text/Icon color (e.g. text-teal-600)
    bgClass: string;   // Used for Border/Accent color (e.g. border-teal-500)
    children?: React.ReactNode;
    selected?: boolean;
    isSource?: boolean;
    isTarget?: boolean;
    hideHandles?: boolean;
    /** When true, show only left (input) and right (output) - no top/bottom. Matches Content Merger style (3 sides: left, right, bottom) when used with threeSidedHandles. */
    compactHandles?: boolean;
    /** When true with transform nodes, show 3 sides only: left, right, bottom (hide top). Matches Content Merger and other workflow nodes. */
    threeSidedHandles?: boolean;
    isRunning?: boolean;
    executionStatus?: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'Success' | 'Running' | 'Failed' | null;
    rowsProcessed?: number;
    className?: string;
}

export function BaseNodeUI({ title, icon: Icon, bgClass, children, selected, isSource = true, isTarget = true, hideHandles = false, compactHandles = false, threeSidedHandles = false, isRunning = false, executionStatus = null, className = "" }: BaseNodeUIProps) {
    // Redesign: Horizontal Card
    // - White Background
    // - Colored Icon Container
    // - Slate Text

    const isTransform = isSource && isTarget;
    const isOnlySource = isSource && !isTarget;
    const isOnlyTarget = !isSource && isTarget;

    // Extract color base from bgClass
    const colorMap: Record<string, { text: string; bg: string }> = {
        teal: { text: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-900/30' },
        blue: { text: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/30' },
        purple: { text: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/30' },
        red: { text: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/30' },
        orange: { text: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/30' },
        amber: { text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/30' },
        yellow: { text: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/30' },
        lime: { text: 'text-lime-600 dark:text-lime-400', bg: 'bg-lime-50 dark:bg-lime-900/30' },
        green: { text: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/30' },
        emerald: { text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/30' },
        cyan: { text: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-900/30' },
        sky: { text: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-900/30' },
        indigo: { text: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/30' },
        violet: { text: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/30' },
        fuchsia: { text: 'text-fuchsia-600 dark:text-fuchsia-400', bg: 'bg-fuchsia-50 dark:bg-fuchsia-900/30' },
        pink: { text: 'text-pink-600 dark:text-pink-400', bg: 'bg-pink-50 dark:bg-pink-900/30' },
        rose: { text: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/30' },
        slate: { text: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-800' },
        gray: { text: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-800' },
        zinc: { text: 'text-zinc-600 dark:text-zinc-400', bg: 'bg-zinc-50 dark:bg-zinc-800' },
        neutral: { text: 'text-neutral-600 dark:text-neutral-400', bg: 'bg-neutral-50 dark:bg-neutral-800' },
        stone: { text: 'text-stone-600 dark:text-stone-400', bg: 'bg-stone-50 dark:bg-stone-800' },
    };

    // Find matching color key based on bgClass inclusion
    const matchedColorKey = Object.keys(colorMap).find(c => bgClass.includes(c));
    const themeParams = matchedColorKey ? colorMap[matchedColorKey] : { text: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-800' };

    const accentText = themeParams.text;
    const accentBg = themeParams.bg;

    // Derived status - Fallback to isRunning for spinner if executionStatus not provided
    const status = executionStatus || (isRunning ? 'Running' : null);

    return (
        <div className="relative flex flex-col items-center group">
            <NodeResizer
                color="#94a3b8"
                isVisible={selected}
                minWidth={180}
                minHeight={60}
                handleStyle={{ width: 8, height: 8, borderRadius: 2 }}
            />

            {/* Main Node Container - Horizontal Card Design */}
            <div
                className={`
                    relative flex items-center
                    w-[200px] h-[64px] rounded-xl shadow-sm border transition-all duration-300 z-10 
                    bg-white dark:bg-slate-900
                    ${selected ? 'border-yellow-400 ring-2 ring-yellow-400/20 shadow-md' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md'}
                    ${className}
                `}
            >
                {/* Colored Icon Container (Left) */}
                <div className={`
                    ml-3 h-10 w-10 flex items-center justify-center rounded-lg 
                    ${accentBg} ${accentText}
                `}>
                    <Icon className="h-5 w-5" strokeWidth={2} />
                </div>

                {/* Label (Right) */}
                <div className="ml-3 flex-1 overflow-hidden">
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate leading-tight select-none" title={title}>
                        {title}
                    </div>
                    {/* Optional: Add a small status text or type if needed later */}
                </div>

                {/* Children render (for extra badges etc) */}
                {children && <div className="mr-2 pointer-events-auto scale-75 origin-right">{children}</div>}

                {/* Status Indicators */}
                {(status === 'RUNNING' || status === 'Running') && (
                    <div className="absolute -top-2 -right-2 p-1 bg-white dark:bg-slate-800 rounded-full shadow-md z-20 border border-slate-200 dark:border-slate-700">
                        <Loader2 className="h-3.5 w-3.5 text-blue-600 animate-spin" strokeWidth={2.5} />
                    </div>
                )}

                {(status === 'COMPLETED' || status === 'Success') && (
                    <div className="absolute -top-2 -right-2 p-1 bg-green-600 dark:bg-green-700 rounded-full shadow-md z-20 border border-white dark:border-slate-800">
                        <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                    </div>
                )}

                {(status === 'FAILED' || status === 'Failed') && (

                    <div className="absolute -top-2 -right-2 p-1 bg-red-600 dark:bg-red-700 rounded-full shadow-md z-20 border border-white dark:border-slate-800">
                        <XCircle className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                    </div>
                )}

                {/* --- HANDLES --- */}

                {/* --- SMART HANDLES (Hidden unless hovered/connected) --- */}
                {/* We add Target/Source handles on all sides. 
                    The Logic in FlowPage will switch edges to use the best handle.
                    We hide them by default to avoid clutter. 
                */}

                {/* TARGETS - always render inputs (except when hideHandles is true) so that any node can be orchestrated/triggered */}
                {!hideHandles && (
                    <>
                        <Handle type="target" position={Position.Left} id="in" className="!w-3 !h-3 !bg-white dark:!bg-slate-400 !border-2 !border-slate-400 z-50" style={{ left: -6 }} />
                        {!compactHandles && !threeSidedHandles && (
                            <Handle type="target" position={Position.Top} id="in-top" className="!w-3 !h-3 !bg-white dark:!bg-slate-400 !border-2 !border-slate-400 z-50 opacity-0 group-hover:opacity-100 transition-opacity" style={{ top: -6 }} />
                        )}
                        {(!compactHandles || threeSidedHandles) && (
                            <Handle type="target" position={Position.Bottom} id="in-bottom" className="!w-3 !h-3 !bg-white dark:!bg-slate-400 !border-2 !border-slate-400 z-50 opacity-0 group-hover:opacity-100 transition-opacity" style={{ bottom: -6 }} />
                        )}
                    </>
                )}

                {/* SOURCES - always render outputs (except when hideHandles is true) so that any node can trigger downstream sequences */}
                {!hideHandles && (
                    <>
                        <Handle
                            type="source" position={Position.Right} id={isTransform || isOnlyTarget ? "success" : "out"}
                            className="!w-3 !h-3 !bg-white !border-2 !border-emerald-500 z-50"
                            style={{ right: -6 }}
                        />
                        {!isOnlyTarget && !compactHandles && !threeSidedHandles && (
                            <>
                                <Handle
                                    type="source" position={Position.Bottom} id={isTransform ? "success-bottom" : "out-bottom"}
                                    className="!w-3 !h-3 !bg-white !border-2 !border-emerald-500 z-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{ bottom: -6 }}
                                />
                                <Handle
                                    type="source" position={Position.Top} id={isTransform ? "success-top" : "out-top"}
                                    className="!w-3 !h-3 !bg-white !border-2 !border-emerald-500 z-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{ top: -6 }}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </div >
    );
}
